import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, type FieldErrors } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBreadcrumb, AppButton, AppCard, AppContainer } from '@/components/atoms';
import { InsurerForm } from '@/components/organisms';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import {
  useCreateInsurerMutation,
  useInsurerCopyDetail,
  useInsurerFormInitData,
} from '@/hooks/admin';
import useAppToast from '@/hooks/useAppToast';
import { insurerFormSchema, type InsurerFormData } from '@/schemas/insurerFormSchema';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';

const SiteAdminCopyInsurerPage: React.FC = () => {
  const navigate = useNavigate();
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'medix';
  const insurerId = Number(id) || 0;

  const toast = useAppToast();
  const { data: initData, isLoading: isInitLoading } = useInsurerFormInitData();
  const {
    data: insurer,
    isLoading: isDetailLoading,
    status: detailStatus,
  } = useInsurerCopyDetail(insurerId);
  const { createInsurerMutation, isPending } = useCreateInsurerMutation(currentSite);

  const methods = useTrimmedForm<InsurerFormData>({
    resolver: zodResolver(insurerFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const breadcrumbItems = [
    { title: '保険者', href: `/${currentSite}/admin/insurers` },
    { title: '作成' },
  ];

  const onValidationError = (_errors: FieldErrors<InsurerFormData>) => {
    toast.error('エラーが発生しました。');
  };

  const onSubmit = async (data: InsurerFormData) => {
    const sanitizedData = {
      ...data,
      cost_rate: (data.cost_rate === '' || data.cost_rate === undefined) ? '0' : data.cost_rate,
      family_cost_rate: (data.family_cost_rate === '' || data.family_cost_rate === undefined) ? '0' : data.family_cost_rate,
      receipt_delegation_from: data.receipt_delegation_from || undefined,
      receipt_delegation_to: data.receipt_delegation_to || undefined,
      available_to: data.available_to || undefined,
    };
    try {
      await createInsurerMutation(sanitizedData);
      navigate(`/${currentSite}/admin/insurers`);
    } catch (error) {
      const apiError = error as { response?: { data?: { errors?: Record<string, string[]> } } };
      const errors = apiError.response?.data?.errors;
      if (errors?.insurer_number) {
        const message = errors.insurer_number[0];
        methods.setError('insurer_number', {
          type: 'server',
          message,
        });
        methods.setError('sub_number', {
          type: 'server',
          message,
        });
      }
      if (errors?.insurer_type_id) {
        toast.error(errors.insurer_type_id[0]);
        methods.setValue('insurer_type_id', initData?.insurer_types[0]?.id ?? 0);
      }
      if (errors?.insurance_kind_id) {
        toast.error(errors.insurance_kind_id[0]);
        methods.setValue('insurance_kind_id', initData?.insurance_kinds[0]?.id ?? 0);
      }
      if (errors?.medix_claim_type_id) {
        toast.error(errors.medix_claim_type_id[0]);
        methods.setValue('medix_claim_type_id', initData?.claim_types[0]?.id ?? 0);
      }
    }
  };

  if (isInitLoading || isDetailLoading) {
    return <LoadingTemplate />;
  }

  if (detailStatus === 'error' || !insurer || !initData) {
    return <SiteAdminNotFoundTemplate />;
  }

  return (
    <AppContainer className="py-6 space-y-6">
      <AppBreadcrumb items={breadcrumbItems} />
      <h3 className="text-xl">保険者作成</h3>
      <AppCard title="基本情報">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onValidationError)}>
            <InsurerForm initData={initData} isEdit insurer={insurer} />
            <div className="flex justify-end">
              <AppButton type="primary" htmlType="submit" loading={isPending}>
                <span className="px-6">更新</span>
              </AppButton>
            </div>
          </form>
        </FormProvider>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminCopyInsurerPage;
